/**
 * Anyway Mobile Drawer — anyway-mobile-drawer.js
 * Vanilla JS, zero dependencies.
 * Dynamic event delegation and state queries for 100% reliability in Shopify Customizer.
 */

(function () {
  'use strict';

  var MOBILE_MAX     = 992;
  var DRAWER_ID      = 'amd-drawer';
  var OVERLAY_ID     = 'amd-overlay';
  var CLOSE_BTN_ID   = 'amd-close-btn';
  var SNACKS_TOGGLE  = '.amd-snacks-toggle';
  var SNACKS_PANEL   = '.amd-snacks-panel';
  var MENU_TOGGLE    = '.menu-toggle';

  function AnywayMobileDrawer() {
    this._isOpen          = false;
    this._scrollY         = 0;
    this._lastFocused     = null;
    this._pinnedSections  = [];

    this._handleKeyDown = this._handleKeyDown.bind(this);
    this._bindEvents();
  }

  AnywayMobileDrawer.prototype.getElements = function () {
    this.drawer     = document.getElementById(DRAWER_ID);
    this.overlay    = document.getElementById(OVERLAY_ID);
    this.closeBtn   = document.getElementById(CLOSE_BTN_ID);
    return this.drawer && this.overlay;
  };

  AnywayMobileDrawer.prototype.open = function () {
    if (!this.getElements()) return;
    if (this._isOpen) return;
    this._isOpen      = true;
    this._lastFocused = document.activeElement;

    // iOS-safe scroll lock
    this._scrollY = window.scrollY || window.pageYOffset;

    // Pin the header group (announcement bar + header) at its CURRENT viewport position
    // BEFORE the body scroll-lock shifts it out of view. This must run before the body is
    // shifted so the captured rects are real viewport coordinates.
    this._pinHeaderGroup();

    document.body.style.overflow  = 'hidden';
    document.body.style.position  = 'fixed';
    document.body.style.top       = '-' + this._scrollY + 'px';
    document.body.style.width     = '100%';

    this.overlay.classList.add('is-open');
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.overlay.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('amd-drawer-open');
    if (window.updateHeaderHeightVar) {
      window.updateHeaderHeightVar();
    }

    // Focus trap
    this.drawer.addEventListener('keydown', this._handleKeyDown);

    // Focus first focusable element
    var self = this;
    requestAnimationFrame(function () {
      var focusable = self._getFocusable();
      if (focusable.length) focusable[0].focus({ preventScroll: true });
    });
  };

  AnywayMobileDrawer.prototype.close = function () {
    if (!this.getElements()) return;
    if (!this._isOpen) return;
    this._isOpen = false;

    // Restore scroll
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top      = '';
    document.body.style.width    = '';

    // Release the header-group sections back into normal document flow
    this._unpinHeaderGroup();

    window.scrollTo(0, this._scrollY);

    this.overlay.classList.remove('is-open');
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.overlay.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('amd-drawer-open');
    if (window.updateHeaderHeightVar) {
      window.updateHeaderHeightVar();
    }

    this.drawer.removeEventListener('keydown', this._handleKeyDown);

    if (this._lastFocused && this._lastFocused.focus) {
      this._lastFocused.focus({ preventScroll: true });
      this._lastFocused = null;
    }
  };

  /**
   * Freeze every visible header-group section (announcement bar, header, etc.) at its current
   * viewport position so the body scroll-lock can't shift it out of view, while preserving the
   * sections' natural vertical stacking. Replaces the old blanket `position:fixed; top:0` CSS
   * which collapsed the announcement bar and header onto each other.
   */
  AnywayMobileDrawer.prototype._pinHeaderGroup = function () {
    var sections = document.querySelectorAll('.shopify-section-group-header-group');
    var data = [];

    // Pass 1 — measure all sections first. Reading every rect up front is essential: the moment
    // a section becomes position:fixed it leaves the flow and shifts the ones below it, so we must
    // capture all positions before mutating any.
    for (var i = 0; i < sections.length; i++) {
      var el = sections[i];
      if (!el.getClientRects().length) continue; // skip disabled/hidden sections (e.g. countdown)
      data.push({
        el: el,
        top: el.getBoundingClientRect().top,
        _position: el.style.position,
        _top: el.style.top,
        _left: el.style.left,
        _width: el.style.width,
        _zIndex: el.style.zIndex
      });
    }

    // Pass 2 — pin each section at its captured viewport top.
    for (var j = 0; j < data.length; j++) {
      var d = data[j];
      d.el.style.position = 'fixed';
      d.el.style.top      = Math.round(d.top) + 'px';
      d.el.style.left     = '0';
      d.el.style.width    = '100%';
      d.el.style.zIndex   = '101';
    }

    this._pinnedSections = data;
  };

  /** Restore the header-group sections' original inline styles (undo _pinHeaderGroup). */
  AnywayMobileDrawer.prototype._unpinHeaderGroup = function () {
    if (!this._pinnedSections || !this._pinnedSections.length) return;
    for (var i = 0; i < this._pinnedSections.length; i++) {
      var d = this._pinnedSections[i];
      d.el.style.position = d._position;
      d.el.style.top      = d._top;
      d.el.style.left     = d._left;
      d.el.style.width    = d._width;
      d.el.style.zIndex   = d._zIndex;
    }
    this._pinnedSections = [];
  };

  AnywayMobileDrawer.prototype._isMobile = function () {
    return window.innerWidth < MOBILE_MAX;
  };

  AnywayMobileDrawer.prototype._bindEvents = function () {
    var self = this;

    // Capture click events globally to resist Customizer section replacements
    document.addEventListener('click', function (e) {
      if (!self._isMobile()) return;

      // Menu toggle clicked (hamburger or close toggle)
      var toggleBtn = e.target.closest(MENU_TOGGLE) || e.target.closest('.amd-menu-btn');
      if (toggleBtn) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (self._isOpen) {
          self.close();
        } else {
          self.open();
        }
        return;
      }

      // Close button clicked inside drawer
      var closeBtn = e.target.closest('#' + CLOSE_BTN_ID);
      if (closeBtn) {
        e.preventDefault();
        self.close();
        return;
      }

      // Overlay clicked
      var overlay = e.target.closest('#' + OVERLAY_ID);
      if (overlay) {
        e.preventDefault();
        self.close();
        return;
      }

      // Accordion click handling
      var accordionToggle = e.target.closest(SNACKS_TOGGLE);
      if (accordionToggle) {
        e.preventDefault();
        var panel = self.drawer ? self.drawer.querySelector(SNACKS_PANEL) : document.querySelector(SNACKS_PANEL);
        if (panel) {
          self._toggleAccordion(accordionToggle, panel);
        }
      }
    }, true);

    // Escape key — document level
    document.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.keyCode === 27) && self._isOpen) {
        e.preventDefault();
        self.close();
      }
    });

    // Resize guard: if open and viewport becomes desktop, close cleanly
    window.addEventListener('resize', function () {
      if (self._isOpen && !self._isMobile()) self.close();
    }, { passive: true });
  };

  AnywayMobileDrawer.prototype._handleKeyDown = function (e) {
    if (e.key !== 'Tab' && e.keyCode !== 9) return;
    var focusable = this._getFocusable();
    if (!focusable.length) return;

    var first = focusable[0];
    var last  = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  AnywayMobileDrawer.prototype._toggleAccordion = function (toggle, panel) {
    var isExpanded = toggle.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      // Animate collapse: set explicit height first, then transition to 0
      panel.style.maxHeight = panel.scrollHeight + 'px';
      panel.getBoundingClientRect(); // force reflow
      panel.style.maxHeight = '0';
      toggle.setAttribute('aria-expanded', 'false');
      panel.classList.remove('is-expanded');
    } else {
      panel.classList.add('is-expanded');
      toggle.setAttribute('aria-expanded', 'true');
      panel.style.maxHeight = panel.scrollHeight + 'px';

      // After transition ends, remove explicit max-height so content is never clipped
      panel.addEventListener('transitionend', function onEnd() {
        if (toggle.getAttribute('aria-expanded') === 'true') {
          panel.style.maxHeight = 'none';
        }
        panel.removeEventListener('transitionend', onEnd);
      });
    }
  };

  AnywayMobileDrawer.prototype._getFocusable = function () {
    if (!this.drawer) return [];
    return Array.prototype.slice.call(
      this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (el) {
      return el.offsetParent !== null;
    });
  };

  // ─── INIT ───────────────────────────────────────────────────
  function init() {
    window.anywayMobileDrawer = new AnywayMobileDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
